/**
 * PropertyMapThumbnail
 * Small static Mapbox map for a property in the AI Import review screen.
 * Geocodes the extracted address, then renders a static image so a reviewer
 * can instantly sanity-check the location (catches geocoding/extraction errors
 * like a wrong state). Degrades to a pin placeholder when there's no token,
 * no address, or geocoding fails.
 */

import { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';

const TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;

interface PropertyMapThumbnailProps {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  className?: string;
}

type Status = 'loading' | 'ok' | 'fail';

// The review step feeds this the live address boxes and Mapbox bills each geocode, so only an address
// that has stopped changing is looked up.
const GEOCODE_DEBOUNCE_MS = 600;

export function PropertyMapThumbnail({
  street,
  city,
  state,
  zip,
  className,
}: PropertyMapThumbnailProps) {
  const query = [street, city, state, zip].filter(Boolean).join(', ').trim();
  const geocodeQuery = useDebounce(query, GEOCODE_DEBOUNCE_MS);
  const [coords, setCoords] = useState<[number, number] | null>(null);
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    if (!TOKEN || !geocodeQuery) {
      setStatus('fail');
      return;
    }
    const controller = new AbortController();
    setStatus('loading');
    (async () => {
      try {
        const res = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(geocodeQuery)}.json?limit=1&country=us&access_token=${TOKEN}`,
          { signal: controller.signal },
        );
        const data = await res.json();
        const center = data?.features?.[0]?.center; // [lng, lat]
        if (controller.signal.aborted) return;
        if (Array.isArray(center) && center.length === 2) {
          setCoords([center[0], center[1]]);
          setStatus('ok');
        } else {
          setStatus('fail');
        }
      } catch {
        if (!controller.signal.aborted) setStatus('fail');
      }
    })();
    return () => controller.abort();
  }, [geocodeQuery]);

  const base = 'rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0';

  if (status !== 'ok' || !coords) {
    return (
      <div className={cn(base, 'text-gray-300 dark:text-gray-600', className)} title={query || 'No address'}>
        <MapPin className="h-5 w-5" />
      </div>
    );
  }

  const [lng, lat] = coords;
  const w = 160;
  const h = 112;
  const url =
    `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/` +
    `pin-s+2563eb(${lng},${lat})/${lng},${lat},11,0/${w}x${h}@2x?access_token=${TOKEN}`;

  return (
    <img
      src={url}
      alt={`Map of ${query}`}
      title={query}
      width={w}
      height={h}
      loading="lazy"
      className={cn('rounded-lg object-cover shrink-0', className)}
    />
  );
}
